import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  Checkbox,
  Stack,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Chip,
  Box,
  ToggleButton,
  Tooltip,
  ToggleButtonGroup,
} from "@mui/material";
import { formatCurrency } from "../utils/currency";
import InvoiceTableToolbar from "../components/invoices/InvoiceTableToolbar";
import SummaryContextLabel from "../components/invoices/SummaryContextLabel";
import InvoicesMonthByMonthChart from "../components/invoices/InvoicesMonthByMonthChart";
import MarkAsPaidDialog from "../components/invoices/MarkAsPaidDialog";
import MuiAlert from "@mui/material/Alert";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import UpgradePrompt from "../components/UpgradePrompt";
import { useTrial } from "../contexts/TrialContext";
import UploadInvoiceDialog from "../components/UploadInvoiceDialog";
import DownloadInvoicesDialog from "../components/invoices/DownloadInvoicesDialog";
import { usePreferences } from "../contexts/PreferencesContext";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "../components/InvoicePDF";
import { Link } from "react-router-dom";
import api from "../services/api";
import { decryptField } from "../utils/encryption";
import GetAppIcon from "@mui/icons-material/GetApp";
import formatDate from "../utils/date";

export default function InvoicesPage() {
  const {
    companyName: prefCompanyName,
    companyAddress: prefCompanyAddress,
    companyNumber: prefCompanyNumber,
    bankDetails: prefBankDetails,
    logoUrl,
    refreshPreferences,
  } = usePreferences();

  const [sending, setSending] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [projects, setProjects] = useState([]);
  const [customMessage, setCustomMessage] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [logoBase64Cached, setLogoBase64Cached] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [markAsPaidOpen, setMarkAsPaidOpen] = useState(false);
  const [selectedToMarkPaid, setSelectedToMarkPaid] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { plan, isTrialExpired } = useTrial();
  const isPro = plan === "pro" || plan === "proplus";
  const isProPlus = plan === "proplus";
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [viewMode, setViewMode] = useState("accrual"); // "accrual" | "cash"

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchProjects = async () => {
      if (!auth.currentUser) return;
      const q = query(
        collection(db, "projects"),
        where("uid", "==", auth.currentUser.uid),
      );
      const snap = await getDocs(q);
      setProjects(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const loadLogo = async () => {
      const base64 = await fetchLogoBase64FromApi();
      if (base64) setLogoBase64Cached(base64);
    };
    loadLogo();
  }, []);

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const fetchInvoices = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, "invoices"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((doc) => {
      const raw = doc.data();
      return {
        id: doc.id,
        ...raw,
        clientName: decryptField(raw.clientName || ""),
        clientEmail: decryptField(raw.clientEmail || ""),
        markedAsPaidAt: raw.markedAsPaidAt
          ? raw.markedAsPaidAt.toDate
            ? raw.markedAsPaidAt.toDate()
            : new Date(raw.markedAsPaidAt)
          : null,
      };
    });

    setInvoices(
      data.sort(
        (a, b) => (b.invoiceDate?.seconds || 0) - (a.invoiceDate?.seconds || 0),
      ),
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchInvoices();
    refreshPreferences();
  }, []);

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "paid" ? "unpaid" : "paid";
      const markedAsPaidAt = newStatus === "paid" ? new Date() : null;

      await updateDoc(doc(db, "invoices", id), {
        status: newStatus,
        markedAsPaidAt,
      });

      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === id
            ? {
                ...inv,
                status: newStatus,
                markedAsPaidAt,
              }
            : inv,
        ),
      );
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Status update failed",
        severity: "error",
      });
    }
  };

  const handleSendInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setClientEmail(invoice.clientEmail || "");
    setCustomMessage(
      `Hi,\n\nPlease find attached invoice #${invoice.invoiceNumber}.\n\nBest regards,`,
    );
    setModalOpen(true);
  };

  const sendEmail = async () => {
    if (!clientEmail || !selectedInvoice) {
      return setSnackbar({
        open: true,
        message: "Client email is required.",
        severity: "error",
      });
    }

    try {
      setSending(true);

      const decryptedInvoice = {
        ...selectedInvoice,
        companyName: selectedInvoice.companyName || prefCompanyName,
        companyAddress:
          decryptField(selectedInvoice.companyAddress) || prefCompanyAddress,
        companyNumber:
          decryptField(selectedInvoice.companyNumber) || prefCompanyNumber,
        clientName: selectedInvoice.clientName,
        clientAddress: decryptField(selectedInvoice.clientAddress),
        bankDetails:
          decryptField(selectedInvoice.bankDetails) || prefBankDetails,
        notes: decryptField(selectedInvoice.notes),
        logoUrl: logoBase64Cached,
      };

      const blob = await pdf(
        <InvoicePDF invoice={decryptedInvoice} />,
      ).toBlob();

      const base64pdf = await blobToBase64(blob);

      await api.post("/invoices/send-invoice", {
        toEmail: clientEmail,
        ccEmail,
        message: customMessage,
        base64pdf,
        invoice: decryptedInvoice,
      });

      setSnackbar({
        open: true,
        message: "Invoice sent successfully.",
        severity: "success",
      });
      setModalOpen(false);
    } catch (err) {
      console.error("Email error", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to send invoice.",
        severity: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;

    try {
      await deleteDoc(doc(db, "invoices", invoiceToDelete.id));
      setInvoices((prev) =>
        prev.filter((inv) => inv.id !== invoiceToDelete.id),
      );
      setSnackbar({
        open: true,
        message: "Invoice deleted.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to delete.",
        severity: "error",
      });
    }

    setConfirmDeleteOpen(false);
    setInvoiceToDelete(null);
  };

  const confirmBulkDelete = async () => {
    try {
      await Promise.all(
        selectedIds.map((id) => deleteDoc(doc(db, "invoices", id))),
      );
      setInvoices((prev) =>
        prev.filter((inv) => !selectedIds.includes(inv.id)),
      );
      setSnackbar({
        open: true,
        message: "Invoices deleted.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Bulk delete failed.",
        severity: "error",
      });
    } finally {
      setSelectedIds([]);
      setConfirmBulkDeleteOpen(false);
    }
  };

  const handleMarkSelectedAsPaid = async () => {
    try {
      await Promise.all(
        selectedIds.map((id) =>
          updateDoc(doc(db, "invoices", id), { status: "paid" }),
        ),
      );

      setInvoices((prev) =>
        prev.map((inv) =>
          selectedIds.includes(inv.id) ? { ...inv, status: "paid" } : inv,
        ),
      );

      setSnackbar({
        open: true,
        message: "Selected invoices marked as paid.",
        severity: "success",
      });

      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk mark as paid failed", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to mark invoices as paid.",
        severity: "error",
      });
    }
  };

  const getRelevantDate = (inv) =>
    viewMode === "accrual"
      ? inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate)
      : inv.markedAsPaidAt
        ? inv.markedAsPaidAt.toDate?.() || new Date(inv.markedAsPaidAt)
        : null;

  const filteredInvoices = invoices.filter((inv) => {
    const date = getRelevantDate(inv);
    if (!date) return false;
    const monthMatch = date.getMonth() + 1 === selectedMonth;
    const yearMatch = date.getFullYear() === selectedYear;
    const statusMatch =
      statusFilter === "all" ? true : inv.status === statusFilter;
    const projectMatch = projectFilter ? inv.projectId === projectFilter : true;

    // 🔍 Search filter: checks invoiceNumber, clientName, projectName
    const q = searchTerm.trim().toLowerCase();
    const client = inv.clientName?.toLowerCase?.() || "";
    const invoiceNumber = String(inv.invoiceNumber || "").toLowerCase();
    const project =
      projects.find((p) => p.id === inv.projectId)?.name?.toLowerCase() || "";

    const searchMatch =
      !q ||
      client.includes(q) ||
      invoiceNumber.includes(q) ||
      project.includes(q);

    return (
      monthMatch && yearMatch && statusMatch && projectMatch && searchMatch
    );
  });

  const paidInvoices = filteredInvoices.filter((inv) => inv.status === "paid");
  const unpaidInvoices = filteredInvoices.filter(
    (inv) => inv.status === "unpaid",
  );

  // Use these helpers for accurate totals in each mode:
  const getMoneyInAmount = (inv) => {
    // Use paidAmountEUR for foreign-currency, otherwise totalAmount (EUR invoices)
    if (viewMode === "cash") {
      if (inv.paidAmountEUR != null) return parseFloat(inv.paidAmountEUR) || 0;
      // fallback for invoices in EUR
      return inv.currency === "EUR" ? parseFloat(inv.paidAmountEUR || 0) : 0;
    } else {
      return parseFloat(inv.totalAmount || 0);
    }
  };

  const getMoneyInDate = (inv) => {
    // Use markedAsPaidAt for money-in, otherwise invoiceDate
    if (viewMode === "cash") {
      if (!inv.markedAsPaidAt) return null;
      // support Firebase Timestamp or ISO/string
      return inv.markedAsPaidAt.toDate?.() || new Date(inv.markedAsPaidAt);
    }
    return inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate);
  };

  const monthlyTotal = invoices
    .filter((inv) => {
      const date = getMoneyInDate(inv);
      return (
        date &&
        date.getMonth() + 1 === selectedMonth &&
        date.getFullYear() === selectedYear
      );
    })
    .reduce((sum, inv) => sum + getMoneyInAmount(inv), 0);

  const yearlyTotal = invoices
    .filter((inv) => {
      const date = getMoneyInDate(inv);
      return date && date.getFullYear() === selectedYear;
    })
    .reduce((sum, inv) => sum + getMoneyInAmount(inv), 0);

  const paidCount = paidInvoices.length;
  const unpaidCount = unpaidInvoices.length;

  const paidTotal = paidInvoices.reduce(
    (sum, inv) => sum + parseFloat(inv.totalAmount || 0),
    0,
  );
  const unpaidTotal = unpaidInvoices.reduce(
    (sum, inv) => sum + parseFloat(inv.totalAmount || 0),
    0,
  );

  const fetchLogoBase64FromApi = async () => {
    try {
      const res = await api.get("/invoices/logo-base64");
      return res.data.base64 || null;
    } catch (err) {
      console.error("❌ Logo Base64 API error", err);
      return null;
    }
  };

  const handleMarkAsPaid = (invoice) => {
    setSelectedToMarkPaid(invoice);
    setMarkAsPaidOpen(true);
  };

  // Called after user confirms "Mark as Paid" dialog
  const onMarkPaid = async (values) => {
    if (!selectedToMarkPaid) return;
    try {
      await updateDoc(doc(db, "invoices", selectedToMarkPaid.id), {
        status: "paid",
        markedAsPaidAt: values.datePaid,
        paidAmountOriginal: values.paidAmountOriginal,
        paidCurrency: values.paidCurrency,
        paidAmountEUR: values.paidAmountEUR,
        exchangeRate: values.exchangeRate,
      });

      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === selectedToMarkPaid.id
            ? {
                ...inv,
                status: "paid",
                markedAsPaidAt: values.datePaid,
                paidAmountOriginal: values.paidAmountOriginal,
                paidCurrency: values.paidCurrency,
                paidAmountEUR: values.paidAmountEUR,
                exchangeRate: values.exchangeRate,
              }
            : inv,
        ),
      );
      setMarkAsPaidOpen(false);
      setSelectedToMarkPaid(null);
      setSnackbar({
        open: true,
        message: "Invoice marked as paid.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to mark as paid.",
        severity: "error",
      });
    }
  };

  // Returns the main currency code if all items in list use the same paidCurrency (or invoice currency), else null
  function getMainCurrency(list, mode = "accrual") {
    const codes = Array.from(
      new Set(
        list
          .map((inv) =>
            mode === "cash"
              ? inv.paidCurrency || inv.currency || "EUR"
              : inv.currency || "EUR",
          )
          .filter(Boolean),
      ),
    );
    return codes.length === 1 ? codes[0] : null;
  }

  // For warning if mixed currencies
  const showAsterisk = (currency) => !currency;

  // Helper to pick correct mode
  const isMoneyInView = viewMode === "cash";

  // ---- Get correct currency for each section ----
  const monthInvoices = invoices.filter((inv) => {
    const d = isMoneyInView
      ? getMoneyInDate(inv)
      : inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate);
    return (
      d &&
      d.getMonth() + 1 === selectedMonth &&
      d.getFullYear() === selectedYear
    );
  });
  const yearInvoices = invoices.filter((inv) => {
    const d = isMoneyInView
      ? getMoneyInDate(inv)
      : inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate);
    return d && d.getFullYear() === selectedYear;
  });

  const monthCurrency = getMainCurrency(
    monthInvoices,
    isMoneyInView ? "cash" : "accrual",
  );
  const yearCurrency = getMainCurrency(
    yearInvoices,
    isMoneyInView ? "cash" : "accrual",
  );
  const paidCurrency = getMainCurrency(
    paidInvoices,
    isMoneyInView ? "cash" : "accrual",
  );
  const unpaidCurrency = getMainCurrency(
    unpaidInvoices,
    isMoneyInView ? "cash" : "accrual",
  );

  // Helper for formatting with fallback
  const formatMoneyIn = (amount, currency) =>
    formatCurrency(amount, currency || "EUR");

  if (loading) return <CircularProgress />;

  return (
    <>
      <UploadInvoiceDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUploaded={fetchInvoices}
      />

      <DownloadInvoicesDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        invoices={invoices}
        prefCompanyName={prefCompanyName}
        prefCompanyAddress={prefCompanyAddress}
        prefCompanyNumber={prefCompanyNumber}
        prefBankDetails={prefBankDetails}
        logoBase64Cached={logoBase64Cached}
      />

      {/* Page Header */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={4}
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} letterSpacing="-0.02em">
            Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {viewMode === "accrual"
              ? "Track invoices by when they were sent"
              : "Track actual payments received"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Tooltip
            title={
              !isPro && isTrialExpired
                ? "Your trial has ended. Upgrade to create new invoices."
                : ""
            }
          >
            <span>
            <Button
              variant="contained"
              component={!isPro && isTrialExpired ? "button" : Link}
              to={
                !isPro && isTrialExpired
                  ? undefined
                  : "/dashboard/invoices/new"
              }
              disabled={!isPro && isTrialExpired}
              sx={{
                opacity: !isPro && isTrialExpired ? 0.6 : 1,
                pointerEvents: !isPro && isTrialExpired ? "none" : "auto",
              }}
            >
              + New Invoice
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="outlined"
          onClick={() => setDownloadDialogOpen(true)}
          sx={{ bgcolor: "background.paper" }}
          startIcon={<GetAppIcon />}
        >
          Export
        </Button>

        <Button
          variant="outlined"
          onClick={() => setUploadDialogOpen(true)}
          sx={{ bgcolor: "background.paper" }}
        >
          Upload Invoice
        </Button>
      </Stack>
      </Box>

      {/* View Mode Toggle */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, val) => val && setViewMode(val)}
          size="small"
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            "& .MuiToggleButton-root": {
              border: "none",
              fontWeight: 600,
              fontSize: 13,
              px: 2.5,
              py: 1,
              textTransform: "none",
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            },
          }}
        >
          <ToggleButton value="accrual">Invoices Sent</ToggleButton>
          <ToggleButton value="cash">Money In</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <SummaryContextLabel
        period="month"
        month={selectedMonth - 1}
        year={selectedYear}
      />

      {/* Summary Stats Grid */}
      <Box mt={3} mb={4}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          {/* This Month */}
          <Paper
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              borderTop: "4px solid",
              borderColor: "primary.main",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}
            >
              This Month
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ letterSpacing: -1 }}
            >
              {formatCurrency(
                monthlyTotal,
                viewMode === "cash" ? "EUR" : monthCurrency || "EUR",
              )}
              {viewMode !== "cash" && showAsterisk(monthCurrency) && "*"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {viewMode === "cash" ? "Received" : "Invoiced"}
            </Typography>
          </Paper>

          {/* This Year */}
          <Paper
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              borderTop: "4px solid",
              borderColor: "secondary.main",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}
            >
              This Year
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ letterSpacing: -1 }}
            >
              {formatCurrency(
                yearlyTotal,
                viewMode === "cash" ? "EUR" : yearCurrency || "EUR",
              )}
              {viewMode !== "cash" && showAsterisk(yearCurrency) && "*"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Year to date
            </Typography>
          </Paper>

          {/* Paid */}
          <Paper
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              borderTop: "4px solid",
              borderColor: "success.main",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}
            >
              Paid
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              color="success.main"
              sx={{ letterSpacing: -1 }}
            >
              {formatMoneyIn(paidTotal, paidCurrency)}
              {showAsterisk(paidCurrency) && "*"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {paidCount} invoice{paidCount !== 1 ? "s" : ""}
            </Typography>
          </Paper>

          {/* Unpaid */}
          <Paper
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              borderTop: "4px solid",
              borderColor: "warning.main",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}
            >
              Outstanding
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              color="warning.main"
              sx={{ letterSpacing: -1 }}
            >
              {formatMoneyIn(unpaidTotal, unpaidCurrency)}
              {showAsterisk(unpaidCurrency) && "*"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {unpaidCount} invoice{unpaidCount !== 1 ? "s" : ""} awaiting
            </Typography>
          </Paper>
        </Stack>

        {/* Mixed currency note */}
        {(showAsterisk(monthCurrency) ||
          showAsterisk(yearCurrency) ||
          showAsterisk(paidCurrency) ||
          showAsterisk(unpaidCurrency)) && (
          <Typography variant="caption" color="text.secondary">
            * Multiple currencies found. Check the invoices table for full
            details.
          </Typography>
        )}
      </Box>

      <InvoicesMonthByMonthChart
        invoices={invoices}
        year={selectedYear}
        viewMode={viewMode}
      />

      {!isPro && isTrialExpired && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#fef3c7",
            border: "1px solid #fde68a",
          }}
        >
          <Typography variant="body2">
            Your trial has ended. You can still view existing invoices, but
            creating or sending new ones requires Pro.
          </Typography>
        </Paper>
      )}

      {filteredInvoices.length === 0 && !isPro && isTrialExpired ? (
        <UpgradePrompt
          source="invoice-page"
          title="🚫 Invoice creation is now disabled."
          subtitle={`Your 14-day trial has ended. Upgrade to ${
            plan === "free" ? "Pro" : "ProPlus"
          } to continue creating and sending invoices.`}
        />
      ) : (
        <Paper sx={{ p: 3, mt: 2 }}>
          <InvoiceTableToolbar
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            projectFilter={projectFilter}
            setProjectFilter={setProjectFilter}
            projects={projects}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />

          {/* Table or Empty State */}
          {filteredInvoices.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", opacity: 0.7 }}>
              <Typography variant="h6" gutterBottom>
                No invoices found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You haven’t created any invoices for this period.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectedIds.length > 0 &&
                        selectedIds.length === filteredInvoices.length
                      }
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < filteredInvoices.length
                      }
                      onChange={(e) =>
                        setSelectedIds(
                          e.target.checked
                            ? filteredInvoices.map((inv) => inv.id)
                            : [],
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Marked as Paid On</TableCell>
                  <TableCell>Status</TableCell>
                  {/* <TableCell>Email Opened</TableCell> */}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(inv.id)}
                        onChange={(e) => {
                          setSelectedIds((prev) =>
                            e.target.checked
                              ? [...prev, inv.id]
                              : prev.filter((id) => id !== inv.id),
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {projects.find((p) => p.id === inv.projectId)?.name ||
                        "—"}
                    </TableCell>
                    <TableCell>
                      {inv.invoiceNumber}
                      {inv.source === "upload" && (
                        <Chip
                          label="Uploaded"
                          size="small"
                          sx={{ ml: 1 }}
                          color="info"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.createdAt?.toDate
                        ? inv.createdAt.toDate().toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell>{inv.clientName}</TableCell>
                    <TableCell>
                      {inv.invoiceDate ? formatDate(inv.invoiceDate) : "—"}
                    </TableCell>
                    <TableCell>
                      <Box>
                        {/* Original invoice amount + currency badge */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {formatCurrency(
                            inv.totalAmount,
                            typeof inv.currency === "string"
                              ? inv.currency
                              : "EUR",
                          )}
                          <Chip
                            label={
                              typeof inv.currency === "string"
                                ? inv.currency
                                : "EUR"
                            }
                            size="small"
                            sx={{ ml: 1, fontWeight: 600, fontSize: 13 }}
                            color="default"
                          />
                        </Box>

                        {/* Show 'Paid in ...' if status=paid and a different paidCurrency exists */}
                        {inv.status === "paid" &&
                          inv.paidAmountOriginal &&
                          inv.paidCurrency &&
                          inv.paidCurrency !== inv.currency && (
                            <Box
                              sx={{
                                mt: 0.5,
                                fontSize: 13,
                                color: "#22c55e",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              Paid:&nbsp;
                              {formatCurrency(
                                inv.paidAmountEUR,
                                inv.paidCurrency,
                              )}
                              <Chip
                                label={inv.paidCurrency}
                                size="small"
                                sx={{
                                  ml: 0.5,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  background: "#e0f2fe",
                                  color: "#0369a1",
                                }}
                                color="default"
                              />
                              {inv.exchangeRate && (
                                <Tooltip
                                  title={`Exchange Rate: ${inv.exchangeRate}`}
                                >
                                  <span
                                    style={{
                                      color: "#888",
                                      marginLeft: 6,
                                      fontSize: 12,
                                    }}
                                  >
                                    (rate: {inv.exchangeRate})
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {inv.markedAsPaidAt ? (
                        <Tooltip
                          title={inv.markedAsPaidAt.toLocaleString("default", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                          arrow
                        >
                          <span>{formatDate(inv.markedAsPaidAt)}</span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        color={inv.status === "paid" ? "warning" : "success"}
                        onClick={() =>
                          inv.status === "paid"
                            ? handleToggleStatus(inv.id, inv.status)
                            : handleMarkAsPaid(inv)
                        }
                      >
                        {inv.status === "paid"
                          ? "Mark as Unpaid"
                          : "Mark as Paid"}
                      </Button>
                    </TableCell>
                    {/* <TableCell>
                      {inv.emailOpenedAt ? (
                        <Typography variant="caption" color="success.main">
                          ✅ Opened
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          📭 Not yet opened
                        </Typography>
                      )}
                    </TableCell> */}
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          disabled={downloadingId === inv.id}
                          onClick={async () => {
                            setDownloadingId(inv.id);
                            try {
                              const blob = await pdf(
                                <InvoicePDF
                                  invoice={{
                                    ...inv,
                                    companyName:
                                      inv.companyName || prefCompanyName,
                                    companyAddress:
                                      decryptField(inv.companyAddress) ||
                                      prefCompanyAddress,
                                    companyNumber:
                                      decryptField(inv.companyNumber) ||
                                      prefCompanyNumber,
                                    clientName: inv.clientName,
                                    clientAddress: decryptField(
                                      inv.clientAddress,
                                    ),
                                    clientCompany: decryptField(
                                      inv.clientCompany,
                                    ),
                                    bankDetails:
                                      decryptField(inv.bankDetails) ||
                                      prefBankDetails,
                                    notes: decryptField(inv.notes),
                                    logoUrl: logoBase64Cached,
                                  }}
                                />,
                              ).toBlob();

                              const link = document.createElement("a");
                              link.href = URL.createObjectURL(blob);
                              link.download = `Invoice_${
                                inv.invoiceNumber || "Generated"
                              }.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(link.href);
                              setSnackbar({
                                open: true,
                                message: "✅ Invoice downloaded successfully!",
                                severity: "success",
                              });
                            } catch (err) {
                              console.error("❌ Failed to generate PDF", err);
                              setSnackbar({
                                open: true,
                                message: "Failed to generate PDF.",
                                severity: "error",
                              });
                            } finally {
                              setDownloadingId(null);
                            }
                          }}
                          title="Download PDF"
                        >
                          {downloadingId === inv.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <GetAppIcon />
                          )}
                        </IconButton>

                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSendInvoice(inv)}
                        >
                          Send
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => {
                            setInvoiceToDelete(inv);
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          Delete
                        </Button>
                        {inv.uploadedUrl && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              window.open(inv.uploadedUrl, "_blank")
                            }
                          >
                            View
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <MarkAsPaidDialog
        open={markAsPaidOpen}
        onClose={() => setMarkAsPaidOpen(false)}
        invoice={selectedToMarkPaid}
        onMarkPaid={onMarkPaid}
      />

      {/* Email Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send Invoice</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Send To (Primary Email)"
            type="email"
            fullWidth
            sx={{ mb: 2 }}
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
          />

          <TextField
            label="CC (Optional)"
            type="email"
            fullWidth
            sx={{ mb: 2 }}
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
          />

          <ReactQuill
            theme="snow"
            value={customMessage}
            onChange={setCustomMessage}
            style={{ height: "200px", marginBottom: "1rem" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={sendEmail}
            disabled={sending}
            startIcon={
              sending ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Delete */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete invoice #
            {invoiceToDelete?.invoiceNumber}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteInvoice}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete */}
      <Dialog
        open={confirmBulkDeleteOpen}
        onClose={() => setConfirmBulkDeleteOpen(false)}
      >
        <DialogTitle>Delete Selected Invoices?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedIds.length} invoice(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDeleteOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmBulkDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
